import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BrandMark, { logoForBrand, useBrandLogos } from './BrandMark';
import HealthReportActions from './HealthReportActions';
import { defaultBusinessProfile, fetchBusinessProfile } from '../lib/businessProfile';
import { downloadPassportPdf, sharePassportPdf } from '../lib/passportPdf';
import { supabase } from '../lib/supabase';
import { scoreBand } from '../lib/healthScore';
import {
  formatDate,
  formatWhen,
  statusLabel,
  vehicleTitle,
} from '../lib/format';
import { isResaleBooking } from '../lib/resaleDesk';
import { useServiceCatalog } from '../hooks/useServiceCatalog';
import { useT } from '../context/LocaleContext';

function groupReportsByVehicle(reports) {
  const map = {};
  for (const report of reports || []) {
    if (!map[report.vehicle_id]) map[report.vehicle_id] = [];
    map[report.vehicle_id].push(report);
  }
  return map;
}

function groupByVehicle(rows, key = 'vehicle_id') {
  const map = {};
  for (const row of rows || []) {
    const id = row[key];
    if (!id) continue;
    if (!map[id]) map[id] = [];
    map[id].push(row);
  }
  return map;
}

function partLabel(row) {
  if (row.category === 'oil') {
    return [row.oil_spec || row.name || 'Oil', row.quantity != null ? `${row.quantity}${row.unit || ''}` : null]
      .filter(Boolean)
      .join(' · ');
  }
  if (row.category === 'tire') {
    return [row.event_type, row.tire_position, row.name].filter(Boolean).join(' · ') || 'Tire work';
  }
  if (row.category === 'battery') return row.name || 'Battery';
  return row.name || row.part_number || 'Part';
}

function quoteTotal(quote) {
  const items = quote?.quotation_items || quote?.items || [];
  return items.reduce((sum, item) => {
    const qty = Number(item.quantity ?? 1);
    const unit = Number(item.unit_price ?? 0);
    const line = item.price != null ? Number(item.price) : qty * unit;
    return sum + (Number.isFinite(line) ? line : 0);
  }, 0);
}

function Fact({ label, value }) {
  return (
    <div className="car-fact">
      <span>{label}</span>
      <strong>{value || '—'}</strong>
    </div>
  );
}

function TimelineRow({ title, meta, end }) {
  return (
    <div className="car-timeline-row">
      <div>
        <strong>{title}</strong>
        {meta ? <p>{meta}</p> : null}
      </div>
      {end || null}
    </div>
  );
}

function receptionMeta(row, t) {
  const date = formatWhen(row.reception_completed_at);
  const mileage =
    row.checkin_mileage != null
      ? `${Number(row.checkin_mileage).toLocaleString()} km`
      : '—';
  const fuel = row.checkin_fuel_level
    ? t(`reception.fuel.${row.checkin_fuel_level}`)
    : '—';
  return t('reception.checkInMeta', { date, mileage, fuel });
}

function CarOverview({
  car,
  visits,
  receptions,
  parts,
  quotations,
  reports,
  onOpenHealth,
  onOpenPassport,
}) {
  const { serviceLabel } = useServiceCatalog();
  const t = useT();
  const latest = reports[0];
  const receptionByBooking = useMemo(() => {
    const map = {};
    for (const row of receptions || []) {
      if (row.booking_id) map[row.booking_id] = row;
    }
    return map;
  }, [receptions]);

  return (
    <div className="car-pane">
      <div className="car-facts">
        <Fact label="Engine" value={car.engine} />
        <Fact label="Transmission" value={car.transmission} />
        <Fact
          label="Mileage"
          value={car.mileage != null ? `${Number(car.mileage).toLocaleString()} km` : null}
        />
        <Fact label="VIN" value={car.vin} />
        <Fact label="Warranty" value={formatDate(car.warranty_expires_at)} />
        <Fact label="Insurance" value={formatDate(car.insurance_expires_at)} />
      </div>

      <div className="car-summary-grid">
        <button type="button" className="car-summary-card" onClick={onOpenHealth}>
          <span className="kicker">Health</span>
          {latest ? (
            <>
              <strong className={`health-score-pill health-band-${scoreBand(latest.overall_score)}`}>
                {latest.overall_score}/100
              </strong>
              <p>Last checked {formatDate(latest.finalized_at)}</p>
            </>
          ) : (
            <p className="empty">No inspection yet</p>
          )}
        </button>
        <button type="button" className="car-summary-card" onClick={onOpenPassport}>
          <span className="kicker">Passport</span>
          <strong>
            {parts.length} part{parts.length === 1 ? '' : 's'}
          </strong>
          <p>
            {visits.length} visit{visits.length === 1 ? '' : 's'} · {quotations.length} quote
            {quotations.length === 1 ? '' : 's'}
          </p>
        </button>
      </div>

      <section className="car-section">
        <div className="car-section-head">
          <h4>{t('reception.checkIns')}</h4>
        </div>
        {(receptions || []).length === 0 ? (
          <p className="empty">{t('reception.checkInsEmpty')}</p>
        ) : (
          receptions.slice(0, 6).map((row) => (
            <TimelineRow
              key={row.id}
              title={serviceLabel(row.bookings?.service_type, 'common.visit')}
              meta={receptionMeta(row, t)}
              end={
                row.booking_id ? (
                  <Link
                    className="ghost linkish"
                    to={`/bookings/${row.booking_id}?view=reception`}
                  >
                    {t('reception.openVisit')}
                  </Link>
                ) : null
              }
            />
          ))
        )}
      </section>

      <section className="car-section">
        <div className="car-section-head">
          <h4>Recent visits</h4>
          {visits.length > 0 ? (
            <button type="button" className="text-link" onClick={onOpenPassport}>
              Full history
            </button>
          ) : null}
        </div>
        {visits.length === 0 ? (
          <p className="empty">No visits for this car yet.</p>
        ) : (
          visits.slice(0, 4).map((row) => {
            const reception = receptionByBooking[row.id];
            return (
              <TimelineRow
                key={row.id}
                title={serviceLabel(row.service_type)}
                meta={[
                  formatWhen(row.scheduled_at || row.created_at),
                  reception
                    ? t('reception.receivedOn', {
                        date: formatDate(reception.reception_completed_at),
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                end={
                  <Link className="ghost linkish" to={`/bookings/${row.id}`}>
                    Open
                  </Link>
                }
              />
            );
          })
        )}
      </section>
    </div>
  );
}

function CarHealthPane({ car, reports, profile }) {
  const { serviceLabel } = useServiceCatalog();
  if (!reports?.length) {
    return (
      <div className="car-pane">
        <p className="empty">
          No finalized health report yet. Open a visit and complete the Health tab to publish one.
        </p>
      </div>
    );
  }

  const latest = reports[0];
  const booking = latest.bookings || {};
  const findings = latest.diagnostic_findings?.trim();

  return (
    <div className="car-pane">
      <div className="car-health-hero">
        <div>
          <span className="kicker">Latest inspection</span>
          <h4>
            {serviceLabel(booking.service_type, 'common.visit')}
          </h4>
          <p>
            {formatDate(latest.finalized_at)}
            {latest.mileage != null
              ? ` · ${Number(latest.mileage).toLocaleString()} km`
              : ''}
          </p>
        </div>
        <strong className={`car-health-score health-band-${scoreBand(latest.overall_score)}`}>
          {latest.overall_score}
          <span>/100</span>
        </strong>
      </div>

      {findings ? (
        <div className="car-alert">
          <span className="kicker">Findings</span>
          <p>{findings}</p>
        </div>
      ) : null}

      <div className="car-actions-bar">
        <div>
          <strong>Health report PDF</strong>
          <p>Branded inspection report for this visit.</p>
        </div>
        <HealthReportActions
          report={latest}
          vehicle={car}
          booking={booking}
          customer={profile}
          className="quote-toolbar compact-toolbar"
        />
      </div>

      {booking.id ? (
        <Link className="ghost linkish" to={`/bookings/${booking.id}?view=health`}>
          Open inspection on visit
        </Link>
      ) : null}

      {reports.length > 1 ? (
        <section className="car-section">
          <div className="car-section-head">
            <h4>Earlier reports</h4>
          </div>
          {reports.slice(1, 5).map((report) => (
            <TimelineRow
              key={report.id}
              title={formatDate(report.finalized_at)}
              meta={serviceLabel(report.bookings?.service_type, 'common.visit')}
              end={
                <span className={`health-score-pill health-band-${scoreBand(report.overall_score)}`}>
                  {report.overall_score}
                </span>
              }
            />
          ))}
        </section>
      ) : null}
    </div>
  );
}

function CarPassportPane({
  car,
  reports,
  parts,
  visits,
  receptions,
  quotations,
  profile,
}) {
  const { serviceLabel } = useServiceCatalog();
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const receptionByBooking = useMemo(() => {
    const map = {};
    for (const row of receptions || []) {
      if (row.booking_id) map[row.booking_id] = row;
    }
    return map;
  }, [receptions]);

  async function exportPdf(share) {
    setBusy(true);
    setError('');
    setOk('');
    try {
      const business = (await fetchBusinessProfile()) || defaultBusinessProfile;
      const payload = {
        business,
        vehicle: car,
        customer: profile,
        healthReports: reports,
        parts,
        bookings: visits,
        quotations,
      };
      if (share) {
        const result = await sharePassportPdf(payload);
        setOk(result === 'shared' ? 'Passport shared.' : 'Sharing unavailable — PDF downloaded.');
      } else {
        await downloadPassportPdf(payload);
        setOk('Passport PDF ready.');
      }
    } catch (err) {
      setError(err?.message || 'Could not export passport.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="car-pane">
      <div className="car-actions-bar">
        <div>
          <strong>Maintenance passport</strong>
          <p>Full history PDF: visits, health, parts, and billing.</p>
        </div>
        <div className="quote-toolbar compact-toolbar">
          <button type="button" className="ghost" disabled={busy} onClick={() => exportPdf(false)}>
            {busy ? 'Preparing…' : 'Download'}
          </button>
          <button type="button" className="primary" disabled={busy} onClick={() => exportPdf(true)}>
            {busy ? 'Preparing…' : 'Share'}
          </button>
        </div>
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      {ok ? <p className="form-ok">{ok}</p> : null}

      <section className="car-section">
        <div className="car-section-head">
          <h4>Parts & fluids</h4>
        </div>
        {parts.length === 0 ? (
          <p className="empty">Nothing logged yet. Use a booking’s Work tab to add oil, battery, tires, or parts.</p>
        ) : (
          parts.slice(0, 10).map((row) => (
            <TimelineRow
              key={row.id}
              title={partLabel(row)}
              meta={[
                row.category || 'spare',
                row.install_date,
                row.notes,
              ]
                .filter(Boolean)
                .join(' · ')}
            />
          ))
        )}
      </section>

      <section className="car-section">
        <div className="car-section-head">
          <h4>{t('reception.checkIns')}</h4>
        </div>
        {(receptions || []).length === 0 ? (
          <p className="empty">{t('reception.checkInsEmpty')}</p>
        ) : (
          receptions.map((row) => (
            <TimelineRow
              key={row.id}
              title={serviceLabel(row.bookings?.service_type, 'common.visit')}
              meta={receptionMeta(row, t)}
              end={
                row.booking_id ? (
                  <Link
                    className="ghost linkish"
                    to={`/bookings/${row.booking_id}?view=reception`}
                  >
                    {t('reception.openVisit')}
                  </Link>
                ) : null
              }
            />
          ))
        )}
      </section>

      <section className="car-section">
        <div className="car-section-head">
          <h4>Visits</h4>
        </div>
        {visits.length === 0 ? (
          <p className="empty">No visits yet.</p>
        ) : (
          visits.slice(0, 8).map((row) => {
            const reception = receptionByBooking[row.id];
            return (
              <TimelineRow
                key={row.id}
                title={serviceLabel(row.service_type)}
                meta={[
                  formatWhen(row.scheduled_at || row.created_at),
                  reception
                    ? t('reception.receivedOn', {
                        date: formatDate(reception.reception_completed_at),
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                end={
                  <>
                    <em className={`pill status-${row.status}`}>{statusLabel(row.status)}</em>
                    <Link className="ghost linkish" to={`/bookings/${row.id}`}>
                      Open
                    </Link>
                  </>
                }
              />
            );
          })
        )}
      </section>

      <section className="car-section">
        <div className="car-section-head">
          <h4>Billing</h4>
        </div>
        {quotations.length === 0 ? (
          <p className="empty">No quotations on file.</p>
        ) : (
          quotations.slice(0, 8).map((row) => (
            <TimelineRow
              key={row.id}
              title={`Quote · ${row.status}`}
              meta={[
                row.total != null ? `EGP ${Number(row.total).toFixed(2)}` : null,
                row.sent_at || row.created_at
                  ? formatDate(row.sent_at || row.created_at)
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
              end={
                row.booking_id ? (
                  <Link className="ghost linkish" to={`/bookings/${row.booking_id}?view=quote`}>
                    Open
                  </Link>
                ) : null
              }
            />
          ))
        )}
      </section>
    </div>
  );
}

function CarDetailBody({
  car,
  reports,
  parts,
  visits,
  receptions,
  quotations,
  profile,
}) {
  const t = useT();
  const [tab, setTab] = useState('overview');

  return (
    <div className="car-detail">
      <div className="car-subtabs" role="tablist" aria-label="Car sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'overview'}
          className={tab === 'overview' ? 'car-subtab on' : 'car-subtab'}
          onClick={() => setTab('overview')}
        >
          {t('customer.overview')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'health'}
          className={tab === 'health' ? 'car-subtab on' : 'car-subtab'}
          onClick={() => setTab('health')}
        >
          {t('customer.health')}
          {reports[0] != null ? <em>{reports[0].overall_score}</em> : null}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'passport'}
          className={tab === 'passport' ? 'car-subtab on' : 'car-subtab'}
          onClick={() => setTab('passport')}
        >
          {t('customer.passport')}
        </button>
      </div>

      {tab === 'overview' ? (
        <CarOverview
          car={car}
          visits={visits}
          receptions={receptions}
          parts={parts}
          quotations={quotations}
          reports={reports}
          onOpenHealth={() => setTab('health')}
          onOpenPassport={() => setTab('passport')}
        />
      ) : null}
      {tab === 'health' ? (
        <CarHealthPane car={car} reports={reports} profile={profile} />
      ) : null}
      {tab === 'passport' ? (
        <CarPassportPane
          car={car}
          reports={reports}
          parts={parts}
          visits={visits}
          receptions={receptions}
          quotations={quotations}
          profile={profile}
        />
      ) : null}
    </div>
  );
}

export default function CustomerPanel({ id, compact = false, focusVehicleId = null }) {
  const t = useT();
  const { serviceLabel } = useServiceCatalog();
  const [view, setView] = useState('cars');
  const [profile, setProfile] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [healthReports, setHealthReports] = useState([]);
  const [parts, setParts] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [openCar, setOpenCar] = useState(focusVehicleId || null);
  const [error, setError] = useState('');
  const makes = useBrandLogos();

  const healthByVehicle = useMemo(
    () => groupReportsByVehicle(healthReports),
    [healthReports],
  );
  const partsByVehicle = useMemo(() => groupByVehicle(parts), [parts]);
  const bookingsByVehicle = useMemo(() => groupByVehicle(bookings), [bookings]);
  const receptionsByVehicle = useMemo(
    () => groupByVehicle(receptions),
    [receptions],
  );
  const quotesByVehicle = useMemo(() => {
    const map = {};
    for (const quote of quotations || []) {
      const vehicleId = quote.bookings?.vehicle_id;
      if (!vehicleId) continue;
      if (!map[vehicleId]) map[vehicleId] = [];
      map[vehicleId].push(quote);
    }
    return map;
  }, [quotations]);

  useEffect(() => {
    let alive = true;
    async function load() {
      const [person, cars, visits] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', id).maybeSingle(),
        supabase
          .from('vehicles')
          .select('*')
          .eq('owner_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('bookings')
          .select('*, vehicles:vehicle_id(brand, model, year, plate_number)')
          .eq('customer_id', id)
          .order('created_at', { ascending: false }),
      ]);
      if (!alive) return;
      const firstError = person.error || cars.error || visits.error;
      if (firstError) {
        setError(firstError.message);
        return;
      }
      const vehicleRows = cars.data || [];
      const visitRows = visits.data || [];
      const vehicleIds = vehicleRows.map((row) => row.id);
      const bookingIds = visitRows.map((row) => row.id);
      let reports = [];
      let partRows = [];
      let quoteRows = [];
      let receptionRows = [];
      if (vehicleIds.length) {
        const [health, workParts, receptionCards] = await Promise.all([
          supabase
            .from('vehicle_health_reports')
            .select(
              '*, bookings:booking_id(id, service_type, scheduled_at, branch_id, branches:branch_id(name, address, phone))',
            )
            .in('vehicle_id', vehicleIds)
            .eq('status', 'finalized')
            .order('finalized_at', { ascending: false }),
          supabase
            .from('job_card_parts')
            .select('*')
            .in('vehicle_id', vehicleIds)
            .order('created_at', { ascending: false }),
          supabase
            .from('job_cards')
            .select(
              'id, vehicle_id, booking_id, reception_completed_at, checkin_mileage, checkin_fuel_level, received_by, bookings:booking_id(id, service_type, scheduled_at, status)',
            )
            .in('vehicle_id', vehicleIds)
            .not('reception_completed_at', 'is', null)
            .order('reception_completed_at', { ascending: false }),
        ]);
        if (!alive) return;
        if (health.error) {
          setError(health.error.message);
          return;
        }
        if (workParts.error) {
          setError(workParts.error.message);
          return;
        }
        if (receptionCards.error) {
          setError(receptionCards.error.message);
          return;
        }
        reports = health.data || [];
        partRows = workParts.data || [];
        receptionRows = receptionCards.data || [];
      }
      if (bookingIds.length) {
        const quotes = await supabase
          .from('quotations')
          .select(
            'id, status, sent_at, created_at, booking_id, bookings:booking_id(vehicle_id), quotation_items(quantity, unit_price, price)',
          )
          .in('booking_id', bookingIds)
          .order('created_at', { ascending: false });
        if (!alive) return;
        if (quotes.error) {
          setError(quotes.error.message);
          return;
        }
        quoteRows = (quotes.data || []).map((row) => ({
          ...row,
          total: quoteTotal(row),
        }));
      }
      setProfile(person.data);
      setVehicles(vehicleRows);
      setBookings(visitRows);
      setHealthReports(reports);
      setParts(partRows);
      setQuotations(quoteRows);
      setReceptions(receptionRows);
      if (focusVehicleId && vehicleRows.some((car) => car.id === focusVehicleId)) {
        setOpenCar(focusVehicleId);
        setView('cars');
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [id, focusVehicleId]);

  if (error) return <p className="form-error">{error}</p>;
  if (!profile) return <p className="empty">{t('customer.loading')}</p>;

  const firstName = (profile.first_name || '').trim();
  const lastName = (profile.last_name || '').trim();
  const fullName = (profile.full_name || '').trim()
    || [firstName, lastName].filter(Boolean).join(' ')
    || '—';
  const initials = [firstName, lastName]
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
    || fullName.charAt(0).toUpperCase()
    || '?';

  const detailRows = [
    { label: t('customer.firstName'), value: firstName || '—' },
    { label: t('customer.lastName'), value: lastName || '—' },
    {
      label: t('customer.email'),
      value: profile.email ? (
        <a href={`mailto:${profile.email}`}>{profile.email}</a>
      ) : (
        '—'
      ),
    },
    {
      label: t('customer.phone'),
      value: profile.phone ? (
        <a href={`tel:${profile.phone}`}>{profile.phone}</a>
      ) : (
        t('customer.noPhone')
      ),
    },
    {
      label: t('customer.memberSince'),
      value: profile.created_at ? formatDate(profile.created_at) : '—',
    },
  ];
  if (profile.role && profile.role !== 'customer') {
    detailRows.push({ label: t('customer.role'), value: profile.role });
  }

  return (
    <div className={`customer-panel${compact ? ' compact' : ''}`}>
      <section className="customer-hero">
        <div className="customer-avatar" aria-hidden="true">
          {initials}
        </div>
        <div className="customer-hero-copy">
          <div className="customer-stat-row">
            <span className="customer-stat">
              <strong>{vehicles.length}</strong>
              {vehicles.length === 1 ? t('customer.carOne') : t('customer.carsCount')}
            </span>
            <span className="customer-stat">
              <strong>{bookings.length}</strong>
              {bookings.length === 1 ? t('customer.visitOne') : t('customer.visitsCount')}
            </span>
          </div>
          <div className="customer-actions">
            {profile.phone ? (
              <a className="customer-action primary" href={`tel:${profile.phone}`}>
                <span className="material-symbol">call</span>
                {t('customer.call')}
              </a>
            ) : null}
            {profile.email ? (
              <a className="customer-action" href={`mailto:${profile.email}`}>
                <span className="material-symbol">mail</span>
                {t('customer.emailAction')}
              </a>
            ) : null}
          </div>
        </div>
      </section>

      <section className="customer-section">
        <header className="customer-section-head">
          <h3>{t('customer.registration')}</h3>
        </header>
        <dl className="customer-dl">
          {detailRows.map((row) => (
            <div key={row.label} className="customer-dl-row">
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="customer-section customer-garage">
        <header className="customer-section-head">
          <h3>{t('customer.garage')}</h3>
        </header>

        <div className="view-tabs customer-tabs" role="tablist">
          <button
            type="button"
            className={view === 'cars' ? 'view-tab on' : 'view-tab'}
            onClick={() => setView('cars')}
          >
            {t('customer.cars')} <em>{vehicles.length}</em>
          </button>
          <button
            type="button"
            className={view === 'visits' ? 'view-tab on' : 'view-tab'}
            onClick={() => setView('visits')}
          >
            {t('customer.visits')} <em>{bookings.length}</em>
          </button>
        </div>

        {view === 'cars' &&
          (vehicles.length === 0 ? (
            <p className="customer-empty">{t('customer.noCars')}</p>
          ) : (
            <div className="booking-list customer-list">
              {vehicles.map((car) => {
                const open = openCar === car.id;
                const carReports = healthByVehicle[car.id] || [];
                const carParts = partsByVehicle[car.id] || [];
                const carVisits = bookingsByVehicle[car.id] || [];
                const carReceptions = receptionsByVehicle[car.id] || [];
                const carQuotes = quotesByVehicle[car.id] || [];
                const latestScore = car.latest_health_score ?? carReports[0]?.overall_score;
                return (
                  <article key={car.id} className={`fold-card${open ? ' open' : ''}`}>
                    <button
                      type="button"
                      className="desk-row as-button"
                      onClick={() => setOpenCar(open ? null : car.id)}
                      aria-expanded={open}
                    >
                      <BrandMark
                        name={car.brand}
                        src={logoForBrand(makes, car.brand)}
                        compact
                      />
                      <div className="desk-main">
                        <h3>{vehicleTitle(car)}</h3>
                        <p>
                          {car.plate_number || t('customer.noPlate')}
                          {car.mileage != null
                            ? ` · ${Number(car.mileage).toLocaleString()} km`
                            : ''}
                          {carReceptions.length
                            ? ` · ${carReceptions.length} reception${carReceptions.length === 1 ? '' : 's'}`
                            : ''}
                        </p>
                      </div>
                      {latestScore != null ? (
                        <span
                          className={`health-score-pill health-band-${scoreBand(latestScore)}`}
                        >
                          Health {latestScore}
                        </span>
                      ) : null}
                      <span className="material-symbol desk-chevron">
                        {open ? 'expand_less' : 'expand_more'}
                      </span>
                    </button>
                    {open ? (
                      <div className="fold-body car-fold-body">
                        <CarDetailBody
                          car={car}
                          reports={carReports}
                          parts={carParts}
                          visits={carVisits}
                          receptions={carReceptions}
                          quotations={carQuotes}
                          profile={profile}
                        />
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ))}

        {view === 'visits' &&
          (bookings.length === 0 ? (
            <p className="customer-empty">{t('customer.noVisits')}</p>
          ) : (
            <div className="booking-list customer-list">
              {bookings.map((row) => (
                <Link
                  key={row.id}
                  className="desk-row"
                  to={
                    isResaleBooking(row)
                      ? `/resale-inspections/booking/${row.id}`
                      : `/bookings/${row.id}`
                  }
                >
                  <BrandMark
                    name={row.vehicles?.brand}
                    src={logoForBrand(makes, row.vehicles?.brand)}
                    compact
                  />
                  <div className="desk-main">
                    <h3>{serviceLabel(row.service_type)}</h3>
                    <p>
                      {vehicleTitle(row.vehicles)} · {formatWhen(row.scheduled_at)}
                    </p>
                  </div>
                  <em className={`pill status-${row.status}`}>{statusLabel(row.status)}</em>
                </Link>
              ))}
            </div>
          ))}
      </section>
    </div>
  );
}
